
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { List, Edit, ArrowLeft, ArrowRight } from 'lucide-react';
import { EditCompany } from '../company/EditCompany';
import CompanyDetails from '../company/CompanyDetails';
import { useCompanyDetails } from '@/hooks/useCompanyDetails';
import { useAppSelector } from '@/hooks/useAppSelector';
// useCompanyScrapbook removed
import { checkAdminAuthorization } from '@/utils/authHelpers';
import { useNavigate } from 'react-router-dom';
import type { Canvas as FabricCanvas } from "fabric/fabric-impl";

interface CompanyDetailViewProps {
  slug: string;
  onBackToList: () => void;
}

const CompanyDetailView = ({
  slug,
  onBackToList
}: CompanyDetailViewProps) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();
  
  // Load company details using the hook
  const { loading, error } = useCompanyDetails({ 
    slug, 
    enabled: true 
  });
  
  // Get company data from Redux store
  const company = useAppSelector(state => state.company.company);
  // Project functionality removed
  const allCompanies = useAppSelector(state => state.supaCompanies.companies);
  
  // Project functionality removed
  
  // Find the current company index for navigation
  const currentIndex = allCompanies.findIndex(c => c.slug === slug);
  const isFirstCompany = currentIndex === 0;
  const isLastCompany = currentIndex === allCompanies.length - 1;

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const isAdminUser = await checkAdminAuthorization(navigate, (isAdmin) => {
        setIsAdmin(isAdmin);
      });
      setIsAdmin(isAdminUser);
    };
    checkAdmin();
  }, [navigate]);

  // Scroll to top of the page when the component mounts or when slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Handle navigation between companies
  const handleNavigate = (direction: 'next' | 'prev') => {
    if (direction === 'next' && !isLastCompany) {
      const nextCompany = allCompanies[currentIndex + 1];
      if (nextCompany) {
        navigate(`/companies/${nextCompany.slug}`);
      }
    } else if (direction === 'prev' && !isFirstCompany) {
      const prevCompany = allCompanies[currentIndex - 1];
      if (prevCompany) {
        navigate(`/companies?slug=${prevCompany.slug}`);
      }
    }
  };

  // Handle toggling project entry
  const handleToggleProject = async () => {
    if (company) {
      await toggleProjectEntry(company);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4 bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-gray-200/50">
          <Button
            variant="ghost"
            onClick={onBackToList}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </Button>
        </div>
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6 flex justify-center items-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600">Loading company details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4 bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-gray-200/50">
          <Button
            variant="ghost"
            onClick={onBackToList}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </Button>
        </div>
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6 flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-600 mb-2">Error Loading Company</h3>
            <p className="text-gray-600 mb-4">{error || "Company details could not be loaded"}</p>
            <Button onClick={onBackToList} variant="outline">
              Return to Companies List
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4 bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-gray-200/50">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={onBackToList}
            className="flex items-center gap-2"
          >
            <List className="h-4 w-4" />
            Back to List
          </Button>
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleNavigate('prev')}
              disabled={isFirstCompany}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleNavigate('next')}
              disabled={isLastCompany}
            >
              Next
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditModalOpen(true)}
            className="ml-auto"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6">
        <CompanyDetails
          company={company}
          onToggleProject={handleToggleProject}
          isInProject={!!matchingEntry}
          onSaveCanvas={async (canvas: FabricCanvas) => {
            return Promise.resolve();
          }}
        />
      </div>
      {isEditModalOpen && company && (
        <EditCompany
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          slug={company.slug}
          currentEmployees={company.employees}
          currentCategory={company.category}
          currentScraper={company.scraper || ""}
        />
      )}
    </div>
  );
};

export default CompanyDetailView;
